import React, { useEffect, useState } from "react";
import styled from "styled-components/macro";
import { useTranslation } from "react-i18next";
import { TertiaryButtonXSmallV2 } from "../Buttons/Buttons";
import { MessageFeedback } from "./MessageFeedback";
import type { ChatMessage, Feedback } from "./types";
import { answerCopyText } from "./answerCopy";
const Row = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.resolve("spacing_sm")};
  align-items: flex-start;
  margin-top: ${({ theme }) => theme.resolve("spacing_md")};
  & > div {
    flex: 1;
  }
  & > div > div:first-child {
    margin-top: 0;
  }
`;
const Status = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;
export function AnswerActions({
  message,
  onSetFeedback,
}: {
  message: ChatMessage;
  onSetFeedback?: (id: string, next: Feedback | null) => void;
}) {
  const { t } = useTranslation();
  const [status, setStatus] = useState("");
  useEffect(() => {
    if (!status) return;
    const timer = window.setTimeout(() => setStatus(""), 2500);
    return () => window.clearTimeout(timer);
  }, [status]);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(answerCopyText(message));
      setStatus(t("Answer copied"));
    } catch {
      setStatus(
        t("Unable to copy the answer. Please select the text to copy it.")
      );
    }
  };
  return (
    <>
      <Row>
        <TertiaryButtonXSmallV2
          type="button"
          aria-label={t("Copy answer")}
          title={status || t("Copy answer")}
          onClick={copy}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            aria-hidden="true"
          >
            {status === t("Answer copied") ? (
              <path d="m5 12 4 4L19 6" />
            ) : (
              <>
                <rect x="8" y="8" width="12" height="12" rx="2" />
                <path d="M16 8V4a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h4" />
              </>
            )}
          </svg>
        </TertiaryButtonXSmallV2>
        {onSetFeedback && message.server_message_id && (
          <div>
            <MessageFeedback
              feedback={message.feedback}
              onChange={(next) => onSetFeedback(message.id, next)}
            />
          </div>
        )}
      </Row>
      {status && <Status role="status">{status}</Status>}
    </>
  );
}
